import { IndexedDBService } from './indexed-db.service';
import { AppBaseDataService } from './app-base-data.service';
import { GlobalService } from './global.service';
import { Inject, Injectable } from '@angular/core';
import { catchError, finalize, map, Observable, of, race, ReplaySubject, shareReplay, Subject, take, tap, timer } from 'rxjs';
import { User } from '../_models/user/user';
import { BaseService } from './base.service';
import { SigninModel } from '../_models/user/signin';
import { ActivatedRoute, Router, RouterStateSnapshot } from '@angular/router';
import { environment } from 'src/environments/environment'
import { WhiteLabelList } from '../_data/white-label-list';
import { Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { NgxSpinnerService } from 'ngx-spinner';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { SignupService } from './signup.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { StepService } from './step.service.service';
import { PlanDataService } from './plan-data.service';
import { CurrencySettingsService } from './currency-settings.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private currentUserSource = new ReplaySubject<User>(1);
  currentUser$ = this.currentUserSource.asObservable();
  currentUserObj: User = null
  checkoutCall = new Subject<void>();
  checkoutCall$ = this.checkoutCall.asObservable();

  private webStoreConfigurationInFlight$: Observable<any> | null = null;
  private webStoreConfigurationCachedResponse: any | null = null;
  private webStoreConfigurationCachedAt = 0;
  private webStoreConfigurationLastFailure: any | null = null;
  private webStoreConfigurationLastFailureAt = 0;

  selectedTimeZone: any = { label: 'United States (New York)', value: 'America/New_York' }
  _globals = GlobalService
  showNewEdit = false
  isViewOnly = false
  activeItem: any = ''
  isNew = false
  isCheckIn = false
  bypassUrls = ['/pages/aboutus', '/pages/emailverified']
  isCheckInEnable: boolean = false;

  constructor(
    private _bs: BaseService,
    private _http: HttpClient,
    private _router: Router,
    private _abd: AppBaseDataService,
    private title: Title,
    private _signupService: SignupService,
    @Inject(DOCUMENT) private _document: HTMLDocument,
    private permissionsService: NgxPermissionsService,
    private stepService: StepService,
    private _currencySettings: CurrencySettingsService,
    private _planData: PlanDataService,
  ) { }

  private clearWebStoreConfigurationCache(): void {
    this.webStoreConfigurationInFlight$ = null;
    this.webStoreConfigurationCachedResponse = null;
    this.webStoreConfigurationCachedAt = 0;
    this.webStoreConfigurationLastFailure = null;
    this.webStoreConfigurationLastFailureAt = 0;
  }

  // Lightweight status check without mutating session/current user
  checkAccountStatus(signin: SigninModel, suppressDeploymentToast = false) {
    if (suppressDeploymentToast) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json; charset=utf-8',
        WLID: this._globals.getWlId() || '',
      });
      return this._http.post(environment.AzureAPIURLForLogin, signin, { headers });
    }

    return this._bs.httpPost(environment.AzureAPIURLForLogin, signin);
  }

  login(signin: SigninModel, isRedirect?: boolean) {

    localStorage.clear()
    this._bs.httpPost(environment.AzureAPIURLForLogin, signin).subscribe((res: any) => {

      if (res.success) {
        sessionStorage.clear();
        // Mark auto-login so signin page can show loader overlay if navigated
        sessionStorage.setItem('auto-login', '1');
        this.stepService.setInProgress(false)
        const model: User = {
          userID: '',
          userName: signin.Username,
          displayName: '',
          token: res.data.access_token,
          rememberMe: true, //signin.rememberMe,
          expires_in: res.data.expires_in,
          refreshToke: res.data.refresh_token,
          photo: '',
          type: ''
        }

        sessionStorage.setItem('login-user', JSON.stringify(signin))
        this.setCurrentUser(model)
        this.autologin(res.data)



      } else {
        if (isRedirect === true) {
          console.error('Auto-login failed during redirect.');
          this._router.navigate(['pages/signin'])
        }
      }
    })
  }

  /** SignIn reports accountStatus 6 / "AccountNotVerified" when step 1 of signup-v6 was done but the OTP never was. */
  private isAccountNotVerified(data: any): boolean {
    // The status lives on the top-level payload or on businessSetup[0] depending on the endpoint.
    return [data, data?.businessSetup?.[0]].some(src => {
      const status = src?.accountStatusString ?? src?.AccountStatusString;
      return status === 'AccountNotVerified' || Number(src?.accountStatus ?? src?.AccountStatus) === 6;
    });
  }

  /** Re-seeds the signup-v6 wizard state from the saved user record (getUserByUserName) and lands on the OTP step (step2). */
  private redirectUnverifiedAccountToOtp(data: any, signin: SigninModel): void {
    const setup = data?.businessSetup?.[0] ?? {};
    const userID = data?.userID ?? data?.userId ?? data?.UserId ?? setup.userID ?? setup.userId ?? '';
    const businessId = setup.businessId ?? setup.BusinessId ?? data?.businessId ?? data?.BusinessId ?? 0;
    const email = (data?.email ?? signin.Username ?? '').trim();

    const personalInfo = {
      userID,
      firstName: data?.firstName ?? '',
      lastName: data?.lastName ?? '',
      email,
      confirmEmail: email,
      password: signin.Password,
      confirmPassword: signin.Password,
      phone: data?.phone ?? data?.userPhone ?? setup.phone ?? setup.businessPhone ?? '',
      companyName: setup.companyName ?? '',
      doAgree: true,
      userExist: false,
    };
    const businessInfo = {
      ...setup,
      businessId,
      userID,
      userId: userID,
      email: setup.email ?? email,
    };

    // Step1 bounces to /signup-v6/sell when no sales channel is stored, so restore the saved choice
    // (defaulting to online store) — otherwise the OTP redirect lands on the sell screen.
    const salesChannels: string[] = [];
    if (setup.hasPOS ?? setup.HasPOS) salesChannels.push('instore');
    if ((setup.standardOnlineStore ?? setup.StandardOnlineStore) || !salesChannels.length) salesChannels.push('online');

    sessionStorage.clear();
    sessionStorage.setItem('login-user', JSON.stringify(signin));
    sessionStorage.setItem('signup-v6-sales-channel', JSON.stringify(salesChannels));
    sessionStorage.setItem('account-verified', 'false');
    sessionStorage.setItem('signupInProgress', '1');
    sessionStorage.setItem('active-step', '1');
    sessionStorage.setItem('personal-Info', JSON.stringify(personalInfo));
    sessionStorage.setItem('business-Info', JSON.stringify(businessInfo));
    sessionStorage.setItem('requiredInfo', JSON.stringify({ BusinessId: businessId, UserId: userID }));
    // Status is already "not verified" server-side; skip the redundant Onboarding save on first step2 load.
    sessionStorage.setItem('account-status-onboarding-saved', '1');

    this.stepService.setInProgress(true);
    this.stepService.setPersonalInfo(personalInfo);
    this.stepService.setBusinessInfo(businessInfo);
    this.stepService.setAccountVerified(false);
    this.stepService.setStep(1);

    this._abd.hideSpinner();
    this._router.navigate(['/signup-v6/step2'], { replaceUrl: true });
  }

  sendForgotpasswordEmail(email: string) {
    let url = environment.BaseURL + environment.ForgotPassword
    return this._bs.httpPost(url, JSON.stringify({
      Username: email
    })).pipe(((response: any) => {
      return response;
    }))
  }

  resetPassword(password: string, userID: string) {
    let url = environment.AzureUserInfo;
    return this._bs.httpPost(url, JSON.stringify({
      "Name": "CHANGEPASSWORD",
      "User": {
        "UserID": userID,
        "Password": password
      }
    })).pipe(((response: any) => {
      return response;
    }))
  }

  changePassword(user: any) {
    let url = environment.AzureUserInfo

    return this._bs.httpPost(url, JSON.stringify({
      "Name": "CHANGEPASSWORD",
      "User": user
    })).pipe(((response: any) => {
      return response;
    }))
  }

  isBypassUrl(url: string) {
    url = url.toLowerCase()
    for (let i = 0; i < this.bypassUrls.length; i++) {
      if (url.includes(this.bypassUrls[i].toLowerCase())) {
        return true
      }
    }
    return false
  }

  loadAppDataFromIndexedDb() { }

  autologin(loginResponseData?: any) {

    window.addEventListener("load", () => {
      try {

        eval('invokeCSharpAction("pageloaded")')
      }
      catch {

      }
    });


    const rawHref = window.location.href || '';
    const decodedHref = decodeURIComponent(rawHref);
    const sourceUrl = decodedHref.toLowerCase()
    let wll: any = null

    // If already on payment success/setup-offer pages, don't force signup redirects.
    if (
      sourceUrl.includes('/signup-v3/subscription-payment-success') ||
      sourceUrl.includes('/signup-v4/subscription-payment-success') ||
      sourceUrl.includes('/signup-v5/subscription-payment-success') ||
      sourceUrl.includes('/signup-v6/subscription-payment-success') ||
      sourceUrl.includes('/signup-v4/store-setup-offer') ||
      sourceUrl.includes('/signup-v5/store-setup-offer') ||
      sourceUrl.includes('/signup-v6/store-setup-offer')
    ) {

      // Keep a dark loader visible so users don't see the signin page flash
      this._abd.showSpinner();
      document.body.classList.add('spinner-dark-redirect');
      sessionStorage.setItem('success-redired', sourceUrl.includes('/signup-v3/subscription-payment-success') ? '0' : '1');
    }

    const isPaymentSuccessUrl =
      sourceUrl.includes('/signup-v3/subscription-payment-success') ||
      sourceUrl.includes('/signup-v4/subscription-payment-success') ||
       sourceUrl.includes('/signup-v5/subscription-payment-success') ||
      sourceUrl.includes('/signup-v6/subscription-payment-success') ||
      sourceUrl.includes('/signup-v4/store-setup-offer') ||
      sourceUrl.includes('/signup-v5/store-setup-offer') ||
      sourceUrl.includes('/signup-v6/store-setup-offer') ||
      sourceUrl.includes('/signup/subscription-payment-success');
    const signupInProgressFlag = sessionStorage.getItem('signupInProgress') === '1';
    const hasSignupQueryParam =
      sourceUrl.includes('/signup') &&
      (sourceUrl.includes('selectedplan') ||
        sourceUrl.includes('selectedPlan') ||
        sourceUrl.includes('plan=') ||
        sourceUrl.includes('email=') ||
        sourceUrl.includes('coupon'));

    if (
      sourceUrl.includes('/signup') &&
      !(this.stepService.isInProgress() || signupInProgressFlag) &&
      !isPaymentSuccessUrl
    ) {
      // If signup data already exists, keep it and mark in progress to avoid clearing on reload
      const hasSignupData =
        sessionStorage.getItem('personal-Info') !== null ||
        sessionStorage.getItem('business-Info') !== null ||
        sessionStorage.getItem('selected_plan_id') !== null;
      if (hasSignupData) {
        this.stepService.setInProgress(true);
        this.ensureSignupFlowFromStoredPlan();
      } else if (hasSignupQueryParam) {
        // Preserve incoming signup deep-link query params without clearing session or redirecting
        this.stepService.setInProgress(true);
        sessionStorage.setItem('signupInProgress', '1');
        sessionStorage.setItem('active-step', '0');
        // Move to the current signup flow while preserving query params
        this._router.navigate(['/signup-v6/step1'], { queryParamsHandling: 'preserve', replaceUrl: true });
      } else {
        // redirect to your Stripe page
        sessionStorage.clear()
        // A brand-new signup entry must never inherit a checkout cart/plan pick left behind by
        // whatever account was last active in this browser (see logoutButtonClicked() for the
        // sign-out side of the same issue) — sessionStorage.clear() above doesn't touch these,
        // they're localStorage.
        localStorage.removeItem('checkout-cart-state')
        localStorage.removeItem('selectedPlanDetails')
        this._router.navigate(['pages/signin']);
        this._abd.showSpinner();

        if (sourceUrl.includes('/signup-v3/subscription-payment-success')) {
          document.body.classList.add('spinner-dark-redirect');
          sessionStorage.setItem('success-redired', sourceUrl.includes('/signup-v3/subscription-payment-success') ? '0' : '1');
        }
      }
    } else if (signupInProgressFlag) {
      // Honor persisted in-progress flag: keep session/local storage intact
      this.stepService.setInProgress(true);
    }

    if (this.isBypassUrl(sourceUrl)) {
      window.location.href = sourceUrl
      return
    }
    // Auto Redirect --
    let url = sourceUrl.split('?')
    if (url[1] !== undefined) {
      if (url[1] === 'categories') {
        let x = window.location.origin + '/#/app/categories?categories'
        this._abd.isRedirect = x
      } else if (url[1] === 'products') {
        let x = window.location.origin + '/#/app/products?products'
        this._abd.isRedirect = x
      }
    }
    // Auto Redirect --
    // if (window.location.host.includes('admin.connect-360') || window.location.host.includes('admin.') || window.location.host.includes('admin.connect-cbd') || window.location.host.includes('admin.com')) {

    wll = WhiteLabelList[0]

    // }

    // else {
    //   if (window.location.host.includes('localhost:')) {
    //     wll = WhiteLabelList[0]

    //   }
    //   else if (window.location.host.includes('connect-360')) {
    //     wll = WhiteLabelList[0]

    //   }
    //   else if (window.location.host.includes('connect-cbd')) {
    //     wll = WhiteLabelList[0]
    //   }
    // }

    // if (!wll) wll = WhiteLabelList[0] // to be deleted later
    let isautoLogin = false

    if (wll) {

      try {
        this._abd.showSpinner()
        this._globals.whiteLabel = wll
        // Login with Redirect In Lincsell
        if (wll.wlName === 'LincSell') {
          const hash = window.location.hash;
          const url = hash.split('ioioio');
          let username = url[2]
          let password = url[4]


          if (username !== undefined && password !== undefined) {
            username = decodeURIComponent(username);
            password = decodeURIComponent(password);
            const signin: SigninModel = {
              Username: username,
              Password: password,
              rememberMe: false
            }
            this._globals.loginErrorMessage = '';
            localStorage.removeItem('user')
            this._abd.showSpinner();
            this.login(signin, true)
            isautoLogin = true
          }
        }


        this.setCurrentUserFromLocalStorage()
        const currentUserName = this.currentUserObj?.userName;
        if (!currentUserName) {
          // A user still in the signup wizard isn't authenticated yet (no token in localStorage).
          // Returning to a signup route — e.g. pressing browser Back from the Stripe checkout —
          // must NOT bounce them to signin; let the already-activated signup step render instead.
          const isSignupInProgress =
            signupInProgressFlag ||
            this.stepService.isInProgress() ||
            sessionStorage.getItem('personal-Info') !== null ||
            sessionStorage.getItem('business-Info') !== null ||
            sessionStorage.getItem('selected_plan_id') !== null;
          if (sourceUrl.includes('/signup') && isSignupInProgress) {
            this.stepService.setInProgress(true);
            this._abd.hideSpinner();
            return;
          }
          sessionStorage.removeItem('auto-login');
          this._abd.hideSpinner();
          this._router.navigate(['pages/signin']);
          return;
        }

        this.getUserByUserName(currentUserName).subscribe(res => {
          if (!(res?.success && res?.data)) {
            sessionStorage.removeItem('auto-login');
            this._abd.hideSpinner();
            this._router.navigate(['pages/signin']);
            return;
          }

          if (res?.success && res?.data) {

            const user = res.data
            this._globals.userInfo = user;
            this.updateSupportWidgetVisibility(user);

            // Signed up (step 1) but never entered the OTP: send them back to the OTP step instead of the dashboard.
            if (this.isAccountNotVerified(user)) {
              let savedSignin: SigninModel | null = null;
              try { savedSignin = JSON.parse(sessionStorage.getItem('login-user') || 'null'); } catch { }
              localStorage.removeItem('user');
              this.currentUserSource.next(null);
              this.currentUserObj = null;
              this.redirectUnverifiedAccountToOtp(
                user,
                savedSignin || { Username: user?.email || currentUserName, Password: '', rememberMe: false }
              );
              return;
            }

            if (!user?.businessSetup?.length) {
              sessionStorage.removeItem('auto-login');
              this._abd.hideSpinner();
              this._router.navigate(['pages/signin']);
              return;
            }
            const isLincsellWebsitePlanLoaded = sessionStorage.getItem('applied-coupon');
            if (!isLincsellWebsitePlanLoaded) {
              if (user?.couponCode && user.couponCode.trim() !== '') {
                const couponCode = user.couponCode;
                sessionStorage.setItem('url_coupon_code', couponCode.toUpperCase());
                this.stepService.setLinkCouponCode(couponCode.toUpperCase());
              }
            }


            const url = window.location.href;
            const isV3Success =
              url.includes('signup-v3/subscription-payment-success') ||
              url.includes('signup/subscription-payment-success');
            const isCheckoutUrl =
              url.includes('/signup-v3/step4') ||
              url.includes('/signup/step4') ||
              url.includes('/signup-v3/checkout') ||
              url.includes('/signup/checkout');
            if ((url.includes('signup/personal-info') || url.includes('signup-v3')) && !isV3Success) {
            
              this._abd.hideSpinner()
              //   if(url.includes('signup/personal-info')){
              //      return  this._router.navigate(['signup/personal-info'])
              //   }
              if (this._globals.userInfo.businessSetup[0].accountStatus == 7) {
                sessionStorage.setItem('account-verified', JSON.stringify(true))

                if (!isCheckoutUrl) {
                  sessionStorage.setItem('active-step', JSON.stringify(2))
                  sessionStorage.setItem('active-step', '2')
                  this.stepService.setStep(2)
                }
                this.stepService.setAccountVerified(true)
              }
              else {
                sessionStorage.setItem('account-verified', JSON.stringify(false))
                this.stepService.setAccountVerified(false);

                this.stepService.setOTPInfoSend(false);
                sessionStorage.setItem('otpSend', '0');
                this.stepService.setOTPCount(0);
                if (!isCheckoutUrl) {
                  sessionStorage.setItem('active-step', '1')
                  this.stepService.setStep(1)
                }


                this.stepService.markBusinessInfoSaved(true);


              }
              this.stepService.setInProgress(true)
              this.stepService.setBusinessInfo(this._globals.userInfo.businessSetup[0]);
              const personalInfo = {
                userID: this._globals.userInfo.userId,
                firstName: this._globals.userInfo.firstName,
                lastName: this._globals.userInfo.lastName,
                email: this._globals.userInfo.email,
                confirmEmail: this._globals.userInfo.email,
                password: '',
                confirmPassword: '',
                phone: this._globals.userInfo.phone,
                companyName: this._globals.userInfo.businessSetup[0].companyName,
                doAgree: true,
                userExist: true,
              }
              if (this._globals.userInfo?.userID != undefined && this._globals.userInfo?.userID != null) {
                personalInfo.userID = this._globals.userInfo?.userID
              }
              sessionStorage.setItem('personal-Info', JSON.stringify(personalInfo))
              sessionStorage.setItem('business-Info', JSON.stringify(this._globals.userInfo.businessSetup[0]))

              this.stepService.setPersonalInfo(personalInfo);

              this.stepService.setBrowseUsingLincsellWebsite(false)
              // const defaultPlan = PlanList[1];
              // defaultPlan.selected = true
              // this.stepService.setUserSelectedPlan(defaultPlan)

              const isLincsellWebsitePlanLoaded = sessionStorage.getItem('lincsell-plan-loaded');
              if (this._globals.userInfo.businessSetup[0].tempSubscriptionPlanID != "" && !isLincsellWebsitePlanLoaded) {
                const parseToInt = parseInt(this._globals.userInfo.businessSetup[0].tempSubscriptionPlanID);
                const defaultPlan = this._planData.list.find(f => f.id == parseToInt);
                const isLincsellWebsitePlanLoaded = sessionStorage.getItem('lincsell-plan-loaded');
                if (defaultPlan !== null && !isLincsellWebsitePlanLoaded) {
                  defaultPlan.selected = true
                  sessionStorage.setItem('url_plan_selected', "1");
                  sessionStorage.setItem('selected_plan_id', JSON.stringify(this._globals.userInfo.businessSetup[0].tempSubscriptionPlanID));
                  this.stepService.setBrowseUsingLincsellWebsite(true)
                  this.stepService.setSelectedPlanId(parseToInt)
                  const storedInterval = sessionStorage.getItem('billing-interval-info');
                  if (storedInterval) {
                    const intervalValue = storedInterval.replace(/"/g, '');
                    this.stepService.setBillingInterval(intervalValue);
                  } else {
                    this.stepService.setBillingInterval(this._globals.userInfo.businessSetup[0].tempBillingInterval.toLowerCase())
                    sessionStorage.setItem('billing-interval-info', JSON.stringify(this._globals.userInfo.businessSetup[0].tempBillingInterval.toLowerCase()));
                  }
                  this.stepService.setUserSelectedPlan(defaultPlan)
                  sessionStorage.setItem('selected-plan-info', JSON.stringify(defaultPlan))
                  this.setSignupFlowFromPlan(defaultPlan);
                }
                else {
                  sessionStorage.setItem('selected-plan-info', JSON.stringify(defaultPlan))
                  this.setSignupFlowFromPlan(defaultPlan);
                }

              }
              if (this._globals.userInfo.businessSetup[0].city != "") {

                const businessAddress = {
                  address: this._globals.userInfo.businessSetup[0].address,
                  address2: "",
                  zipCode: this._globals.userInfo.businessSetup[0].zip,
                  city: this._globals.userInfo.businessSetup[0].city,
                }
                sessionStorage.setItem('business-address-state', JSON.stringify(this._globals.userInfo.businessSetup[0].state))
                sessionStorage.setItem('business-address', JSON.stringify(businessAddress))
                this.stepService.setBusinessAddress(businessAddress);
                this.stepService.setBusinessAddressSaved(true)

                sessionStorage.setItem('active-step', '3')
                this.stepService.setStep(3)
              }



              if (isCheckoutUrl) {
                return;
              }
              return this._router.navigate([this.getSignupEntryRoute()])
            }

            sessionStorage.setItem('un-verified-account', "0")
            // //// Signup-Redirect

            // A completed signup that landed on the Free plan is neither "on a free trial" nor has
            // a "completed subscription" — isFreeTrial/isSubscriptionCompleted are both false for
            // it, exactly like a genuinely abandoned mid-signup account. Without a bypass, every
            // successful Free-plan signup falls into this block and gets force-navigated back into
            // the signup wizard on every subsequent login, forever (the loop this guards against) —
            // never reaching the hasLocations check further down this function that correctly
            // recognizes a finished setup, because this block already returns first.
            //
            // accountStatusString is the authoritative signal: subscription-payment-success's own
            // deployment poll (checkDeploymentStatus()) already treats any status other than
            // 'DeploymentInProgress' as "account ready" — same rule applied here. hasLocationsAlready
            // is kept as a secondary check, but userLocations/​_abd.locations may not be populated
            // yet at this point in autologin() even for a genuinely finished account (confirmed via a
            // real businessSetup payload where NoOfLocations was 1 but this array was still empty).
            const accountStatusString = res.data.businessSetup[0].accountStatusString || res.data.businessSetup[0].AccountStatusString || '';
            const isDeploymentDone = !!accountStatusString && accountStatusString !== 'DeploymentInProgress';
            const hasLocationsAlready =
              (this._globals.userInfo?.userLocations && this._globals.userInfo.userLocations.length > 0) ||
              (this._abd.locations && this._abd.locations.length > 0);

            if (!isDeploymentDone && !hasLocationsAlready && !res.data.businessSetup[0].isFreeTrial && !res.data.businessSetup[0].isSubscriptionCompleted
            ) {
              sessionStorage.setItem('un-verified-account', "1")
              this._signupService.setInitialSubscriptionDetail(this._globals.userInfo);

              // let selPlans:any = sessionStorage.getItem('selected-plans')
              let selPlans: any = sessionStorage.getItem('checkout-signup-subscription')

              let selhardWare: any = sessionStorage.getItem('selected-hardwares')
              let selLoc: any = sessionStorage.getItem('selected-additional-loc-reg')
              let personal: any = sessionStorage.getItem('personal-Info')
              let business: any = sessionStorage.getItem('business-Info')
              let interval: any = sessionStorage.getItem('interval')
              if (selPlans !== undefined) {
                this._signupService.selectedPlans = JSON.parse(selPlans)
              }
              if (selhardWare !== null) {
                this._signupService.selectedHardware = JSON.parse(selhardWare)
              }
              if (selLoc !== null) {
                this._signupService.selectedLocationsAndRegistersList = JSON.parse(selLoc)
              }
              if (personal !== null) {
                this._signupService.personalInfo = JSON.parse(personal)
              }
              if (business !== null) {
                this._signupService.businessInfo = JSON.parse(business)
              }
              if (interval !== null) {
                // Stored as a raw 'm'/'y' shorthand by app.component.ts (sessionStorage.setItem('interval', iv)),
                // not JSON — JSON.parse(interval) throws on this value. selectedPlanInterval expects
                // the full capitalized word, same as every other setter of this property.
                this._signupService.selectedPlanInterval = interval === 'm' ? 'Monthly' : 'Yearly'
              }
              sessionStorage.setItem('is-Redirect', 'Yes')
              const personalInfo = {
                userID: this._globals.userInfo.userId,
                firstName: this._globals.userInfo.firstName,
                lastName: this._globals.userInfo.lastName,
                email: this._globals.userInfo.email,
                confirmEmail: this._globals.userInfo.email,
                password: '',
                confirmPassword: '',
                phone: this._globals.userInfo.phone,
                companyName: this._globals.userInfo.businessSetup[0].companyName,
                doAgree: true,
                userExist: true,
              }
              sessionStorage.setItem('personal-Info', JSON.stringify(personalInfo))
              sessionStorage.setItem('business-Info', JSON.stringify(this._globals.userInfo.businessSetup[0]))

              if (this._globals.userInfo.businessSetup[0].accountStatus == 7) {
                sessionStorage.setItem('active-step', JSON.stringify(2))
                sessionStorage.setItem('account-verified', JSON.stringify(true))

                sessionStorage.setItem('active-step', '2')

                this.stepService.setStep(2)
                this.stepService.setInProgress(true)
                this.stepService.setBusinessInfo(this._globals.userInfo.businessSetup[0]);
                this.stepService.setPersonalInfo(personalInfo);
                this.stepService.setAccountVerified(true)
                this.stepService.setBrowseUsingLincsellWebsite(false)
                let defaultPlan = this._planData.list[1];
                defaultPlan.selected = true
                this.stepService.setUserSelectedPlan(defaultPlan)
                if (this._globals.userInfo.businessSetup[0].city != "") {
                  const businessAddress = {
                    address: this._globals.userInfo.businessSetup[0].address,
                    address2: "",
                    zipCode: this._globals.userInfo.businessSetup[0].zip,
                    city: this._globals.userInfo.businessSetup[0].city,
                  }
                  sessionStorage.setItem('business-address-state', JSON.stringify(this._globals.userInfo.businessSetup[0].state))
                  sessionStorage.setItem('business-address', JSON.stringify(businessAddress))
                  this.stepService.setBusinessAddress(businessAddress);
                  this.stepService.setBusinessAddressSaved(true)
                }

                const isLincsellWebsitePlanLoaded = sessionStorage.getItem('lincsell-plan-loaded');
                if (this._globals.userInfo.businessSetup[0].tempSubscriptionPlanID != "" && !isLincsellWebsitePlanLoaded) {
                  const parseToInt = parseInt(this._globals.userInfo.businessSetup[0].tempSubscriptionPlanID);
                  const defaultPlanFound = this._planData.list.find(f => f.id == parseToInt);

                  if (defaultPlanFound !== null) {
                    defaultPlanFound.selected = true
                    this.stepService.setSelectedPlanId(parseToInt)
                    sessionStorage.setItem('url_plan_selected', "1");
                    sessionStorage.setItem('selected_plan_id', JSON.stringify(this._globals.userInfo.businessSetup[0].tempSubscriptionPlanID));
                    this.stepService.setBrowseUsingLincsellWebsite(true)
                    this.stepService.setBillingInterval(this._globals.userInfo.businessSetup[0].tempBillingInterval.toLowerCase())
                    sessionStorage.setItem('billing-interval-info', JSON.stringify(this._globals.userInfo.businessSetup[0].tempBillingInterval.toLowerCase()));
                    this.stepService.setUserSelectedPlan(defaultPlanFound)
                    sessionStorage.setItem('selected-plan-info', JSON.stringify(defaultPlanFound))
                    defaultPlan = defaultPlanFound
                    this.setSignupFlowFromPlan(defaultPlanFound);
                  }
                  else {
                    sessionStorage.setItem('selected-plan-info', JSON.stringify(defaultPlan))
                    this.setSignupFlowFromPlan(defaultPlan);
                  }
                }

                sessionStorage.setItem('selected-plan-info', JSON.stringify(defaultPlan))
                this.setSignupFlowFromPlan(defaultPlan);

                this._router.navigate([this.getSignupStep1Route()])
              }
              else {

                sessionStorage.setItem('otpSend', '0');
                this.stepService.setOTPCount(0);
                this.stepService.setBusinessInfo(this._globals.userInfo.businessSetup[0]);
                const personalInfo = {
                  userID: this._globals.userInfo.userID,
                  firstName: this._globals.userInfo.firstName,
                  lastName: this._globals.userInfo.lastName,
                  email: this._globals.userInfo.email,
                  confirmEmail: this._globals.userInfo.email,
                  password: '',
                  confirmPassword: '',
                  phone: this._globals.userInfo.phone,
                  companyName: this._globals.userInfo.businessSetup[0].companyName,
                  doAgree: true,
                  userExist: true,
                }
                sessionStorage.setItem('personal-Info', JSON.stringify(personalInfo))
                sessionStorage.setItem('business-Info', JSON.stringify(this._globals.userInfo.businessSetup[0]))

                this.stepService.setPersonalInfo(personalInfo);
                sessionStorage.setItem('active-step', JSON.stringify(0))
                localStorage.removeItem('goto');
                localStorage.removeItem('goto-prev');
                sessionStorage.setItem('account-verified', JSON.stringify(false))
                sessionStorage.setItem('active-step', '1')
                sessionStorage.setItem('relogin', JSON.stringify(true))

                sessionStorage.setItem('otpSend', '0');
                sessionStorage.removeItem('otp-send-lock');
                this.stepService.setStep(1)
                this.stepService.markBusinessInfoSaved(true);
                this.stepService.setAccountVerified(false);
                this.stepService.setInProgress(true)


                this.stepService.setOTPInfoSend(false);
                this.stepService.setOTPCount(0);
                this._router.navigate([this.getSignupStep1Route()])
              }

              this._abd.hideSpinner()
              return this.checkoutCall.next();
            }
            this.primeCurrentUserState(user);
            if (wll.wlName === 'LincSell') {
              this.clearWebStoreConfigurationCache();
              this.refreshMenuForCurrentUser();
              this.getWebStoreConfiguration().subscribe({
                next: (res) => {
                  if (res?.success) {
                    this._globals.webStoreConfiguration = res.data;
                  }
                  this.refreshMenuForCurrentUser();
                },
                error: () => {
                  this.refreshMenuForCurrentUser();
                }
              })
            } else {
              this.refreshMenuForCurrentUser();
            }

            // //// Signup-Redirect
            if (this._globals.userInfo.changePassword) {
              this._abd.hideSpinner()
              this._router.navigate(['pages/change-password'])
            } else {

              if (res.data.businessSetup[0].isSubscriptionCompleted || res.data.businessSetup[0].isFreeTrial) {
                this.getUserRoles()
              }

              this._document.getElementById('appFavicon')?.setAttribute('href', 'assets/images/' + wll.favIcon + '.ico')
              this.title.setTitle(wll.wlName)
              const hasWarmFilters = this._abd.hydrateAllFiltersFromCache()
              const loadInitRequest$ = this._abd.loadInit().pipe(
                catchError(() =>
                  of({
                    success: false,
                    data: this._globals.userInfo
                  })
                ),
                shareReplay({ bufferSize: 1, refCount: false })
              )

              // Continue loading fresh filters in background even if we route on timeout fallback.
              loadInitRequest$.subscribe({
                error: () => {
                }
              })

              const loadInitFallback$ = timer(hasWarmFilters ? 1200 : 3000).pipe(
                map(() => ({
                  success: true,
                  data: this._globals.userInfo,
                  fromWarmStart: true
                }))
              )

              race(loadInitRequest$, loadInitFallback$).pipe(take(1)).subscribe((res1: any) => {
            

                if (!res1?.success) {
                  this._abd.hydrateAllFiltersFromCache()
                }

                // ---------------------------------
                user.isBusinessSetup = true;
                // ---------------------------------

                if (!user.isBusinessSetup) {
                  console.warn('[hideSpinner] → Business not setup');
                  this._abd.hideSpinner();
                  return this._router.navigate(['setup/business-setup']);
                }

                const url = window.location.href;
                const host = window.location.host;
                const goto = localStorage.getItem('goto');
                const autoLogin = sessionStorage.getItem('auto-login');
                const isSignupFlowUrl = url.includes('/signup') || url.includes('/signup-v3');
                const isStoreSetupRoute =
                  url.includes('/welcome-store-setup') ||
                  url.includes('/welcome-store-setup-v2');
                const shouldUseGoto = !isSignupFlowUrl;

                // ----------------------------------------------------
                // HANDHELD
                // ----------------------------------------------------
                if (url.includes('/handheld')) {
                  console.warn('[hideSpinner] → Handheld redirect');
                  this._abd.hideSpinner();
                  return this._router.navigate(['handheld/inventory-report']);
                }

                // ----------------------------------------------------
                // ADMIN SUPER USER
                // ----------------------------------------------------
                const adminDomains = [
                  'admin.connect',
                  'admin.lincsell',
                  'preadmin.lincsell',
                  'localhost:4200',
                  'admin.saasypos',
                  'lsocalhost'
                ];

                if (
                  adminDomains.some(d => host.includes(d)) &&
                  this._globals.userInfo?.username === 'user.admin@lincsell.com'
                ) {
                  console.warn('[hideSpinner] → Admin super user redirect');
                  this._abd.hideSpinner();
                  return this._router.navigate(['bizz/customers-list']);
                }

                // ----------------------------------------------------
                // IN-APP CHECKOUT (Buy more / plan checkout) — never auto-redirect away from an
                // in-progress checkout on a plain reload. Once `goto` matches the current URL it
                // gets cleared the moment it's consumed, so the *next* reload has no goto to match
                // and every branch below falls through to the dashboard-route fallback — a real
                // Router navigation away from the already-mounted PaymentPageComponent that trips
                // its "Leave checkout?" unsaved-changes guard on nothing more than a refresh.
                // Excludes the older signup/check-out route, which has its own handling below.
                // ----------------------------------------------------
                if (url.includes('/check-out') && !url.includes('signup/check-out')) {
                  console.warn('[hideSpinner] → In-app checkout, staying put');
                  this._abd.hideSpinner();
                  return;
                }

                // ----------------------------------------------------
                // LINCSELL BRAND
                // ----------------------------------------------------
                if (wll.wlName === 'LincSell') {

                  // CHECKOUT REDIRECT
                  if (url.includes('signup/check-out')) {
                    console.warn('[hideSpinner] → Checkout redirect');
                    this.restoreSignupSession();
                    sessionStorage.setItem('is-Redirect', 'Yes');
                    this._abd.hideSpinner();
                    return this.checkoutCall.next();
                  }

                  // PAYMENT SUCCESS
                  if (url.includes('signup/subscription-payment-success') && !autoLogin) {
                    console.warn('[hideSpinner] → Payment success (v1)');
                    this._abd.hideSpinner();
                    return this._router.navigate(['signup/subscription-payment-success']);
                  }

                  if (url.includes('signup-v3/subscription-payment-success') && !autoLogin) {
                    console.warn('[hideSpinner] → Payment success (v3)');
                    // this._abd.hideSpinner();
                    return this._router.navigate(['signup-v3/subscription-payment-success']);
                  }

                  if (url.includes('signup-v4/subscription-payment-success') && !autoLogin) {
                    this._abd.hideSpinner();
                    return this._router.navigate(['signup-v4/subscription-payment-success']);
                  }

                  if (url.includes('signup-v4/store-setup-offer') && !autoLogin) {
                    sessionStorage.setItem('allow-store-setup-offer', '1');
                    sessionStorage.setItem('signup-v4-post-payment-stage', 'offer');
                    this._abd.hideSpinner();
                    return this._router.navigate(['signup-v4/store-setup-offer']);
                  }

                  if (url.includes('signup-v5/subscription-payment-success') && !autoLogin) {
                    this._abd.hideSpinner();
                    return this._router.navigate(['signup-v5/subscription-payment-success']);
                  }

                  if (url.includes('signup-v5/store-setup-offer') && !autoLogin) {
                    sessionStorage.setItem('allow-store-setup-offer', '1');
                    sessionStorage.setItem('signup-v5-post-payment-stage', 'offer');
                    this._abd.hideSpinner();
                    return this._router.navigate(['signup-v5/store-setup-offer']);
                  }

                  if (url.includes('signup-v6/subscription-payment-success') && !autoLogin) {
                    this._abd.hideSpinner();
                    return this._router.navigate(['signup-v6/subscription-payment-success']);
                  }

                  if (url.includes('signup-v6/store-setup-offer') && !autoLogin) {
                    sessionStorage.setItem('allow-store-setup-offer', '1');
                    sessionStorage.setItem('signup-v6-post-payment-stage', 'offer');
                    this._abd.hideSpinner();
                    return this._router.navigate(['signup-v6/store-setup-offer']);
                  }

                  sessionStorage.removeItem('auto-login');
                  // The welcome-store page opened by URL in a fresh tab (e.g. from the setup checklist): go there
                  // instead of the dashboard. The auth guard runs again on this navigation, now that the user is loaded.
                  const postLoginRoute = GlobalService.takePostLoginRoute();
                  if (postLoginRoute) {
                    this._abd.hideSpinner();
                    return this._router.navigateByUrl(postLoginRoute);
                  }
                  if (window.location.hash.startsWith('#/welcome-store')) {
                    this._abd.hideSpinner();
                    return this._router.navigateByUrl('/welcome-store');
                  }


                  // FIRST LOGIN LOGIC
                  const isFirstLogin = user?.businessSetup?.[0]?.isFirstLogin;

                  const isBusinessSetup = user?.businessSetup?.[0]?.isBusinessSetup;

                  const isFreeTrial = res1.data?.businessSetup?.[0]?.isFreeTrial;
                  const selectPlan = localStorage.getItem('is_Select_Plan');
                  const hasLocations =
                    (this._globals.userInfo?.userLocations && this._globals.userInfo.userLocations.length > 0) ||
                    (this._abd.locations && this._abd.locations.length > 0);

                  if (!hasLocations) {
                    // store-setup-offer / welcome-store-setup(-v2) are removed from the flow —
                    // onboarding now happens via the business-setup checklist on the dashboard itself.
                    // A default location still has to exist before we get there though: dashboard-v2
                    // and most other pages assume _abd.locations has at least one entry and crash
                    // otherwise (seen for DeploymentCompleted accounts with 0 locations on login).
                    // Only DeploymentCompleted accounts get a default location here — a
                    // DeploymentInProgress account has 0 locations because provisioning hasn't
                    // finished yet, not because it needs one created; the auth guard already routes
                    // those to the deployment-pending waiting screen.
                    // Temporarily hidden: no longer calls SetLocationSetup on login (default location is
                    // created server-side during signup).
                    // const accountStatusString = user?.businessSetup?.[0]?.accountStatusString || user?.businessSetup?.[0]?.AccountStatusString || '';
                    // if (accountStatusString === 'DeploymentCompleted' || accountStatusString === 'ActivatedAccount' ) {
                    //   console.warn('[hideSpinner] No locations -> create default + dashboard');
                    //   this.craeteDefaultLocation();
                    // }
                    this.clearSignupStateStorage();
                    this._abd.hideSpinner();
                    return this._router.navigateByUrl(this._abd.getDashboardRoute());
                  }
                  const SetupFlag = localStorage.getItem('showSetup') === 'true';
                  if (SetupFlag) {
                    console.warn('[hideSpinner] Setup flag -> dashboard');
                    this._abd.hideSpinner();
                    return this._router.navigateByUrl(this._abd.getDashboardRoute());
                  }

                  // if (isFirstLogin === true ) {
                  //   console.warn('[hideSpinner] First login flag true → welcome');
                  //   this.clearSignupStateStorage();
                  //   this._abd.hideSpinner();
                  //   return this._router.navigate(['welcome']);
                  // }

                  // if (isFirstLogin === false && localStorage.getItem('isFirstLogin') !== 'true') {
                  //   console.warn('[hideSpinner] → First login default-location');
                  //   this.craeteDefaultLocation();
                  //   this.clearSignupStateStorage();
                  //   //  this._abd.hideSpinner();
                  //   return this._router.navigate(['welcome']);
                  // }

                  if (isFirstLogin === true && !selectPlan && isFreeTrial && !isStoreSetupRoute) {
                    this._abd.hideSpinner();
                    return this._router.navigateByUrl(this._abd.getDashboardRoute());
                  }

                  if (isStoreSetupRoute) {
                    this._abd.hideSpinner();
                    return;
                  }

                  // GENERIC REDIRECTS
                  if (shouldUseGoto && goto?.includes('/pages/signin') && !isStoreSetupRoute) {
                    this._abd.hideSpinner();
                    return this._router.navigateByUrl(this._abd.getDashboardRoute());
                  }

                  if (this._abd.isRedirect) {
                    this._abd.hideSpinner();
                    return (window.location.href = this._abd.isRedirect);
                  }

                  if (shouldUseGoto && goto) {
                    localStorage.removeItem('goto');
                    this._abd.hideSpinner();
                    return (window.location.href = goto + '/');
                  }

                  const loginUser = sessionStorage.getItem('login-user');
                  if (loginUser) {
                    sessionStorage.removeItem('login-user')
                    sessionStorage.removeItem('signupInProgress')
                    sessionStorage.removeItem('un-verified-account')

                  }
                  // fallback
                  this._abd.hideSpinner();
                  return this._router.navigateByUrl(this._abd.getDashboardRoute());
                }

                // ----------------------------------------------------
                // OTHER BRANDS
                // ----------------------------------------------------
                if (shouldUseGoto && goto?.includes('/pages/signin')) {
                  this._abd.hideSpinner();
                  return this._router.navigateByUrl(this._abd.getDashboardRoute());
                }

                if (shouldUseGoto && goto) {
                  console.warn('[hideSpinner] → Non-lincs → goto redirect');
                  localStorage.removeItem('goto');
                  this._abd.hideSpinner();
                  return (window.location.href = goto + '/');
                }

                console.warn('[hideSpinner] → Non-lincs fallback');
                this._abd.hideSpinner();
                return this._router.navigateByUrl(this._abd.getDashboardRoute());
              });


            }

          }
        }, (error) => {
          localStorage.clear()
          sessionStorage.clear()

          this._router.navigate(['pages/signin'])
          console.error('Error fetching user data during autologin:', error);
          this._abd.hideSpinner()
        })
      } catch {
        if (window.location.href.includes('/handheld'))
          this._router.navigate(['handheld/signin'])
        else {

          const url = window.location.href;
          const autoLogin = sessionStorage.getItem('auto-login');
          let planId;

          if (url.includes('signup/check-out')) {
        
            this._abd.hideSpinner()
            let selPlans: any = sessionStorage.getItem('checkout-signup-subscription')
            let selhardWare: any = sessionStorage.getItem('selected-hardwares')
            let selLoc: any = sessionStorage.getItem('selected-additional-loc-reg')
            let personal: any = sessionStorage.getItem('personal-Info')
            let business: any = sessionStorage.getItem('business-Info')
            let interval: any = sessionStorage.getItem('interval')
            if (selPlans !== undefined) {
              this._signupService.selectedPlans = JSON.parse(selPlans)
            }
            if (selhardWare !== null) {
              this._signupService.selectedHardware = JSON.parse(selhardWare)
            }
            if (selLoc !== null) {
              this._signupService.selectedLocationsAndRegistersList = JSON.parse(selLoc)
            }
            if (personal !== null) {
              this._signupService.personalInfo = JSON.parse(personal)
            }
            if (business !== null) {
              this._signupService.businessInfo = JSON.parse(business)
            }
            if (interval !== null) {
              // Same fix as the other selectedPlanInterval block above — 'interval' is stored as a
              // raw 'm'/'y' string, not JSON.
              this._signupService.selectedPlanInterval = interval === 'm' ? 'Monthly' : 'Yearly'
            }
            let isRedirect = sessionStorage.getItem('is-Redirect')
            if (isRedirect !== null && isRedirect === 'Yes') {
              return this._router.navigate(['signup/check-out'])
            } else {
              //return  this._router.navigate(['signup/personal-info'])

              sessionStorage.setItem('active-step', '2')

              this.stepService.setStep(2)

              return this._router.navigate([this.getSignupEntryRoute()])
            }
            // const httpParams = new HttpParams({ fromString: url.split('?')[1] });
            // planId = httpParams.get("selected_plan");
          }
          if (url.includes('signup/subscription-payment-success') && !autoLogin) {
            this._abd.hideSpinner()
            return this._router.navigate(['signup/subscription-payment-success'])
          }
          if (url.includes('signup-v3/subscription-payment-success') && !autoLogin) {
            //  this._abd.hideSpinner();
            return this._router.navigate(['signup-v3/subscription-payment-success']);
          }
          if (url.includes('signup-v4/subscription-payment-success') && !autoLogin) {
            this._abd.hideSpinner();
            return this._router.navigate(['signup-v4/subscription-payment-success']);
          }
          if (url.includes('signup-v4/store-setup-offer') && !autoLogin) {
            sessionStorage.setItem('allow-store-setup-offer', '1');
            sessionStorage.setItem('signup-v4-post-payment-stage', 'offer');
            this._abd.hideSpinner();
            return this._router.navigate(['signup-v4/store-setup-offer']);
          }
          if (url.includes('signup-v5/subscription-payment-success') && !autoLogin) {
            this._abd.hideSpinner();
            return this._router.navigate(['signup-v5/subscription-payment-success']);
          }
          if (url.includes('signup-v5/store-setup-offer') && !autoLogin) {
            sessionStorage.setItem('allow-store-setup-offer', '1');
            sessionStorage.setItem('signup-v5-post-payment-stage', 'offer');
            this._abd.hideSpinner();
            return this._router.navigate(['signup-v5/store-setup-offer']);
          }
          if (url.includes('signup-v6/subscription-payment-success') && !autoLogin) {
            this._abd.hideSpinner();
            return this._router.navigate(['signup-v6/subscription-payment-success']);
          }
          if (url.includes('signup-v6/store-setup-offer') && !autoLogin) {
            sessionStorage.setItem('allow-store-setup-offer', '1');
            sessionStorage.setItem('signup-v6-post-payment-stage', 'offer');
            this._abd.hideSpinner();
            return this._router.navigate(['signup-v6/store-setup-offer']);
          }
          else if (url.includes('signup')) {
            this._abd.hideSpinner()
            //else if(url.includes('signup/personal-info')){
            return this._router.navigate([this.getSignupEntryRoute()])
            //return  this._router.navigate(['signup/personal-info'])
          }
          //  else if(url.includes('/setup/signup')){
          //  if(sessionStorage.getItem('selectedPlan') !== null){
          //   this._signupService.selectedPlan = JSON.parse(sessionStorage.getItem('selectedPlan'))
          //  }
          //   this._abd.hideSpinner()
          //   return  this._router.navigate(['/setup/signup'])
          //  }

          if (planId != "" && planId != null) {
            sessionStorage.setItem('selected-from-business-website', "1")
          }
          else {

            if (isautoLogin === true) {
              this._router.navigateByUrl(this._abd.getDashboardRoute())
            } else {
              this._router.navigate(['pages/signin'])
            }
          }
        }
        this._abd.hideSpinner()
      }
    } else {
      this._router.navigate(['/error']);
    }
  }
  loadPermissions(userRights: any[]) {
    const rights = Array.isArray(userRights)
      ? userRights.filter((right) => right !== null && right !== undefined && right !== '')
      : [];
    this.permissionsService?.flushPermissions();
    return this.permissionsService?.loadPermissions(rights);
  }

  private getNormalizedUserRights(user: any): any[] {
    const rights = user?.userRights ?? user?.UserRights ?? [];
    return Array.isArray(rights) ? rights : [];
  }

  private primeCurrentUserState(user: any): void {
    this._globals.userInfo = user;
    this._currencySettings.syncCurrencyFromBusinessSetup(user);

    const setup = user?.businessSetup?.[0];
    if (setup) {
      sessionStorage.setItem('business-Info', JSON.stringify(setup));
    }

    this.loadPermissions(this.getNormalizedUserRights(user));
    this.updateSupportWidgetVisibility(user);
  }

  // Roles that should never see the Zendesk support launcher (e.g. 1001 = Business Manager).
  private static readonly SUPPORT_WIDGET_HIDDEN_ROLE_IDS = ['1001'];

  private updateSupportWidgetVisibility(user: any): void {
    const roles: any[] = user?.role ?? user?.Role ?? [];
    const shouldHide = Array.isArray(roles)
      && roles.some(r => UserService.SUPPORT_WIDGET_HIDDEN_ROLE_IDS.includes(String(r)));
    this.toggleSupportWidget(!shouldHide);
  }

  private toggleSupportWidget(show: boolean, attempt = 0): void {
    const zE = (window as any).zE;
    if (typeof zE === 'function') {
      // This Zendesk account is on the new Messaging Web SDK — the old `webWidget` Classic
      // API (e.g. `zE('webWidget', 'hide')`) was retired and throws "method does not exist".
      zE('messenger', show ? 'show' : 'hide');
      return;
    }
    // The Zendesk snippet (index.html) loads asynchronously and is skipped on localhost,
    // so `zE` may not exist yet (or ever) — retry briefly instead of failing silently.
    if (attempt < 20) {
      setTimeout(() => this.toggleSupportWidget(show, attempt + 1), 300);
    }
  }

  private refreshMenuForCurrentUser(): void {
    this._abd.initMenuList(false);
  }

  private restoreSignupSession() {
    const fields = {
      'checkout-signup-subscription': 'selectedPlans',
      'selected-hardwares': 'selectedHardware',
      'selected-additional-loc-reg': 'selectedLocationsAndRegistersList',
      'personal-Info': 'personalInfo',
      'business-Info': 'businessInfo',
      'interval': 'selectedPlanInterval'
    };

    Object.entries(fields).forEach(([key, serviceField]) => {
      const val = sessionStorage.getItem(key);
      if (val !== null) this._signupService[serviceField] = JSON.parse(val);
    });
  }


  public triggerCheckout(): void {
    this.checkoutCall.next();
  }
  getTimeAttendance(userID: any) {
    let url = environment.BusinessBaseURL + 'GetTimeAttendanceReport'
    let obj = {
      UserId: userID,
      Report: 0,
      PageNo: 0,
      PageSize: 1,
    }
    return this._bs.httpPost(url, obj).pipe(map(res => {
      return res;
    }))
  }
  checkInUser(queryParem: any, userData: any) {
    let url = environment.BusinessBaseURL + `SetTimeAttendance?DateTimeEnable=true&CheckInTime=${queryParem}`
    return this._bs.httpPost(url, userData).pipe(map(res => {
      return res;
    }))
  }
  checkOutUser(queryParem: any, userData: any) {
    let url = environment.BusinessBaseURL + `SetTimeAttendance?DateTimeEnable=true&CheckInTime=${queryParem}`
    return this._bs.httpPost(url, userData).pipe(map(res => {
      return res;
    }))
  }
  getUserRoles() {
    const url = environment.BusinessBaseURL + environment.GetRightsGroup
    this._bs.httpGet(url).subscribe((res: any) => {
      if (res.success) {
        this._globals.userRoles = res.data
      }
    })
  }
  getWebStoreConfiguration(forceRefresh: boolean = false): Observable<any> {
    const ttlMs = 5 * 60 * 1000;
    const failureCooldownMs = 30 * 1000;

    if (forceRefresh) {
      this.clearWebStoreConfigurationCache();
    }

    const now = Date.now();
    if (
      !forceRefresh &&
      this.webStoreConfigurationCachedResponse &&
      now - this.webStoreConfigurationCachedAt < ttlMs
    ) {
      return of(this.webStoreConfigurationCachedResponse);
    }

    if (this.webStoreConfigurationInFlight$) {
      return this.webStoreConfigurationInFlight$;
    }

    if (
      !forceRefresh &&
      this.webStoreConfigurationLastFailure &&
      now - this.webStoreConfigurationLastFailureAt < failureCooldownMs
    ) {
      return of(this.webStoreConfigurationLastFailure);
    }

    const url = environment.BusinessBaseURL + "webstore/configuration";
    this.webStoreConfigurationInFlight$ = this._bs.httpGet(url).pipe(
      tap((res: any) => {
        if (res?.success) {
          this.webStoreConfigurationCachedResponse = res;
          this.webStoreConfigurationCachedAt = Date.now();
          this.webStoreConfigurationLastFailure = null;
          this.webStoreConfigurationLastFailureAt = 0;
          return;
        }

        this.webStoreConfigurationCachedResponse = null;
        this.webStoreConfigurationCachedAt = 0;
        this.webStoreConfigurationLastFailure = res;
        this.webStoreConfigurationLastFailureAt = Date.now();
      }),
      finalize(() => {
        this.webStoreConfigurationInFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.webStoreConfigurationInFlight$;
  }
  getRoles() {
    const url = environment.BusinessBaseURL + environment.GetRoles
    return this._bs.httpGet(url).pipe(map((res: any) => {
      return res
    }))
  }

  /**
   * Ensure signup-flow is tracked in session based on the current plan.
   * Once marked as paid, we don't downgrade to free unless explicitly cleared.
   */
  private ensureSignupFlowFromStoredPlan(): void {
    if (sessionStorage.getItem('signup-flow')) return;
    const planRaw = sessionStorage.getItem('selected-plan-info');
    if (!planRaw) return;
    try {
      const plan = JSON.parse(planRaw);
      this.setSignupFlowFromPlan(plan);
    } catch {
      // ignore parse errors
    }
  }

  private setSignupFlowFromPlan(plan: any): void {
    if (!plan) return;
    const existing = sessionStorage.getItem('signup-flow');
    if (existing === 'paid') return; // never downgrade
    const isFree = this.isPlanFree(plan);
    sessionStorage.setItem('signup-flow', isFree ? 'free' : 'paid');
  }

  private isPlanFree(plan: any): boolean {
    const planIdRaw =
      plan?.PlanId ??
      plan?.PlanID ??
      plan?.SubscriptionPlanID ??
      plan?.id ??
      plan?.planId;
    const planId = planIdRaw !== undefined && planIdRaw !== null ? parseInt(planIdRaw, 10) : NaN;
    const hasPlanId = !isNaN(planId);
    const isFreeId = hasPlanId && planId === 1;
    return isFreeId || plan?.freeSubscription === true;
  }

  private getSignupEntryRoute(): string {
    return '/signup-v6/step1';
  }

  private getSignupStep1Route(): string {
    return '/signup-v6/step1';
  }
  getTimeAttendaceList(obj) {
    let url = environment.InventoryBaseURL + `Dashboard/GetEmployeeTimeStatus?TimeZone=${obj.value}`
    return this._bs.httpGet(url).pipe(map((res: any) => {
      return res
    }))
  }

  private clearSignupStateStorage(): void {
    const sessionKeys = [
      'signupInProgress',
      'signup-complete',
      'signup-flow',
      'account-verified',
      'active-step',
      'personal-Info',
      'business-Info',
      'business-address',
      'selected-plan-info',
      'selected_plan_id',
      'billing-interval-info',
      'prefill-email',
      'prefill-email-checked-value',
      'logout-prompt-choice',
      'logout-prompt-active',
      'skip-otp-redirect'
    ];
    sessionKeys.forEach(k => sessionStorage.removeItem(k));

    const localKeys = [
      'goto',
      'goto-prev',
      'url_plan_selected',
      'otp-count',
      'otpSend',
      'interval',
      'un-verified-account',
      'prefill-email-checked',
      // Checkout/cart state — must not survive to the next login/signup, otherwise a plan
      // picked (and possibly purchased) by the previous account shows up pre-selected.
      'selectedPlanDetails',
      'checkout-cart-state',
    ];
    localKeys.forEach(k => localStorage.removeItem(k));

    const cartSessionKeys = [
      'checkout-usage-intent',
      'selectedHardware',
      'selectedWebsiteAndOnlineStorePlan',
      'selectedPlanTimeframe',
      'selectedPlan',
      'locationsQty',
      'registersQty',
      'accountVerified',
    ];
    cartSessionKeys.forEach(k => sessionStorage.removeItem(k));
  }

  craeteDefaultLocation(details?: {
    businessDisplayName?: string;
    businessName?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    phone?: string;
  }) {
    let sessionSetup: any = null;
    try {
      sessionSetup = JSON.parse(sessionStorage.getItem('business-Info') || 'null');
    } catch {
      sessionSetup = null;
    }

    const setup = this._globals.userInfo?.businessSetup?.[0] || sessionSetup || {};
    const businessId = setup?.businessId ?? setup?.id ?? null;
    const currentUrl = this._router.url || '';
    const isBusinessSetupRoute = currentUrl.includes('/setup/business-setup');
    const isWelcomeSetupRoute =
      currentUrl.includes('/welcome-store-setup-v2') ||
      currentUrl.includes('/welcome-store-setup') ||
      currentUrl.includes('/signup-v4/store-setup-offer') ||
      currentUrl.includes('/signup-v5/store-setup-offer') ||
      currentUrl.includes('/signup-v6/store-setup-offer');
    const locationName = (isBusinessSetupRoute || isWelcomeSetupRoute)
      ? 'Default Location'
      : (
        details?.city ||
        setup?.city ||
        'Default Location'
      );
    let obj = {
      LocationName: locationName,
      ManageInventory: true,
      Phone: String(details?.phone || setup?.phone || '').replace(/\D/g, ''),
      Address: details?.address || setup?.address,
      City: details?.city || setup?.city,
      State: details?.state || setup?.state,
      zip: String(details?.zip || setup?.zip || '').slice(0, 5),
      Country: details?.country || setup?.country || "US",
      LocationId: businessId,
      AllowShipping: true,
      AcceptOnlineOrders: true,
    }

    if (!obj.LocationId) {
      return;
    }

    const normalizedLocationName = String(obj.LocationName || '').trim().toLowerCase();
    const normalizedAddress = String(obj.Address || '').trim().toLowerCase();
    const normalizedCity = String(obj.City || '').trim().toLowerCase();
    const normalizedState = String(obj.State || '').trim().toLowerCase();
    const normalizedZip = String(obj.zip || '').trim();
    const hasMatchingDefaultLocation = Array.isArray(this._abd.locations) && this._abd.locations.some((location: any) => {
      const locationId = String(location?.locationId || location?.LocationId || '').trim();
      const existingLocationName = String(location?.locationName || location?.LocationName || '').trim().toLowerCase();
      const existingAddress = String(location?.address || location?.Address || '').trim().toLowerCase();
      const existingCity = String(location?.city || location?.City || '').trim().toLowerCase();
      const existingState = String(location?.state || location?.State || '').trim().toLowerCase();
      const existingZip = String(location?.zip || location?.Zip || location?.zipcode || location?.zipCode || '').trim();

      return locationId === String(obj.LocationId)
        || (
          existingLocationName === normalizedLocationName
          && existingAddress === normalizedAddress
          && existingCity === normalizedCity
          && existingState === normalizedState
          && existingZip === normalizedZip
        );
    });

    if (hasMatchingDefaultLocation) {
      this._abd.loadInit().subscribe();
      return;
    }

    const url = environment.BusinessBaseURL + environment.CreateLocation;
    const isWelcomeRoute = isWelcomeSetupRoute;
    const maxLocationsMessage = "Business reached to it's max number of locations!";
    const alreadyExistsMessage = "Default Location Already Exists!";

    const shouldRetryMaxLocationFailure = (source: any): boolean => {
      if (source?.success === true) {
        return false;
      }

      const responseMessage = String(source?.message || source?.error?.message || '').trim();
      const codeText = String(source?.code || source?.error?.code || '').toUpperCase();

      if (responseMessage === maxLocationsMessage) {
        return true;
      }

      // Keep a fallback when backend sends a structured max-location code without the message text.
      if (codeText.includes('MAXLOCATION')) {
        return true;
      }

      return false;
    };

    // The backend already has a default location for this business — nothing to retry,
    // and there's no failure to surface to the user.
    const isAlreadyExistsFailure = (source: any): boolean => {
      if (source?.success === true) {
        return false;
      }

      const responseMessage = String(source?.message || source?.error?.message || '').trim();
      const codeText = String(source?.code || source?.error?.code || '').toUpperCase();

      return responseMessage === alreadyExistsMessage || codeText === 'AMS0007919';
    };

    const maxRetryAttempts = 5;
    const maxBackoffDelayMs = 30000;
    const getRetryDelayMs = (attempt: number): number => Math.min(1000 * Math.pow(2, attempt - 1), maxBackoffDelayMs);

    const tryCreateDefaultLocation = (attempt: number = 1): void => {
      // skipProcessErrors: true — processErrors() fires a global toast for any message-bearing
      // failure and then `return`s undefined instead of the response (base.service.ts), so the
      // "Default Location Already Exists!" case this method already handles as a normal, silent
      // outcome (see isAlreadyExistsFailure below) never reached it as such: the real message was
      // lost, the check couldn't match, and it kept retrying against an endpoint that had already
      // told it the location was there. Going straight to the raw response here preserves the
      // message this method needs and skips the unwanted user-facing toast for an expected state.
      this._bs.httpPost(url, JSON.stringify(obj), true, true, null, true).pipe(
        map((res: any) => {
          return res;
        })
      ).subscribe({
        next: (res: any) => {
          if (isAlreadyExistsFailure(res)) {
            this._abd.loadInit().subscribe();
            return;
          }

          const isMaxLocationFailure = isWelcomeRoute && res?.success !== true && shouldRetryMaxLocationFailure(res);
          const isOtherFailure = res?.success !== true && !isMaxLocationFailure;

          const shouldRetryBecauseApiFailed =
            (isMaxLocationFailure && attempt < 2)
            || (isOtherFailure && attempt < maxRetryAttempts);

          if (shouldRetryBecauseApiFailed) {
            setTimeout(() => tryCreateDefaultLocation(attempt + 1), getRetryDelayMs(attempt));
            return;
          }

          this._abd.loadInit().subscribe();
        },
        error: (error: any) => {
          if (isAlreadyExistsFailure(error)) {
            this._abd.loadInit().subscribe();
            return;
          }

          const isMaxLocationFailure = isWelcomeRoute && shouldRetryMaxLocationFailure(error);

          const shouldRetryBecauseApiFailed =
            (isMaxLocationFailure && attempt < 2)
            || (!isMaxLocationFailure && attempt < maxRetryAttempts);

          if (shouldRetryBecauseApiFailed) {
            setTimeout(() => tryCreateDefaultLocation(attempt + 1), getRetryDelayMs(attempt));
            return;
          }

          this._abd.loadInit().subscribe();
        }
      });
    };

    tryCreateDefaultLocation();
  }
  getPosRights() {
    const url = environment.BusinessBaseURL + 'GetBusinessRightsStructured'
    const obj = {
      //"plateform": "BusinessDashboard"
      "plateform": "POSAPP"
      // "plateform": "All"
    }
    return this._bs.httpPost(url, obj).pipe(map(res => {
      return res;
    }))
  }

  getBackOfficeRights() {
    const url = environment.BusinessBaseURL + 'GetBusinessRightsStructured'
    const obj = {
      "plateform": "BusinessDashboard"
      // "plateform": "POSAPP"
      // "plateform": "All"
    }
    return this._bs.httpPost(url, obj).pipe(map(res => {
      return res;
    }))
  }

  getRights(platform: string = 'All') {
    const url = environment.BusinessBaseURL + environment.GetRights
    return this._bs.httpPost(url, JSON.stringify({ "plateform": platform }))
      .pipe(map(res => {
        return res;
      }))
  }

  getRightGroups() {
    let url = environment.BusinessBaseURL + environment.GetRightsGroup;

    return this._bs.httpGet(url).pipe(map(res => {
      return res;
    }))
  }

  getUserByUserName(name: string) {
    if (this.shouldUseCachedAdminUserOnCustomersList(name)) {
      return of({ success: true, data: this._globals.userInfo }).pipe(map((res) => res));
    }

    const model = {
      Name: "GETUSERBYUSERNAME",
      User: {
        Username: name
      }
    }
    return this._bs.httpPost(environment.AzureUserInfo, model, true, false).pipe(map(res => {
      return res
    }))
  }

  private shouldUseCachedAdminUserOnCustomersList(name: string): boolean {
    const username = String(name || '').trim().toLowerCase();
    const cachedUsername = String(this._globals.userInfo?.username || '').trim().toLowerCase();
    if (!username || !cachedUsername || username !== cachedUsername) {
      return false;
    }

    const hasBusinessSetup = Array.isArray(this._globals.userInfo?.businessSetup) && this._globals.userInfo.businessSetup.length > 0;
    if (!hasBusinessSetup) {
      return false;
    }

    const host = String(window?.location?.host || '').toLowerCase();
    const adminDomains = ['admin.connect', 'admin.lincsell', 'preadmin.lincsell', 'localhost:4200', 'admin.saasypos', 'lsocalhost'];
    const isAdminSuperUser = adminDomains.some((d) => host.includes(d)) && cachedUsername === 'user.admin@lincsell.com';
    if (!isAdminSuperUser) {
      return false;
    }

    const hashPath = String(window?.location?.hash || '').toLowerCase();
    const href = String(window?.location?.href || '').toLowerCase();
    return hashPath.includes('/bizz/customers-list') || href.includes('/bizz/customers-list');
  }

  setCurrentUser(user: User) {
    //const dt = Date.now() + (1 * 60 * 60 * 1000)
    const dt = Date.now() + ((user.expires_in - 3600) * 1000)
    localStorage.setItem('lastLoginDT', dt + '');

    const userString = JSON.stringify(user)
    localStorage.setItem('user', userString);
    this.currentUserObj = JSON.parse(userString)
    this.currentUserSource.next(user);
  }

  setCurrentUserFromLocalStorage() {
    const user = JSON.parse(localStorage.getItem('user'))
    if (user) {
      this.currentUserObj = user
      this.currentUserSource.next(user);
    }
  }

  getUserToken(): any {
    if (!this.currentUser$) return null
    if (this.currentUserObj.token.length < 20) return null
    return this.currentUserObj.token
  }

  setUserTokenExpiryTime() {
    const today = new Date()
    const timeObject = new Date(today.getTime() + (this.currentUserObj.expires_in * 1000))
    this.currentUserObj.tokenExpiryTime = timeObject
    this.setCurrentUser(this.currentUserObj)
  }

  isUserAutoLogin() {
    const model = JSON.parse(localStorage.getItem('user'));
    if (!model) return false
    if (!this.isUserTokenExpired() && model.rememberMe) return true
    return false
  }

  isUserTokenExpired() {
    const model = JSON.parse(localStorage.getItem('user'));
    if (!model) return false
    const tdate = new Date(model.tokenExpiryTime)
    const today = new Date()
    return today > tdate ? true : false
  }

  getRefreshToken(model) {
    const url = `${environment.AuthenticationURL}RefreshToken`

    return this._bs.httpPost(url, JSON.stringify(model)).pipe(map(res => {
      return res;
    }))
  }

  logout() {

    try {

      if (window.location.href.includes('/handheld')) {
        
        eval('invokeCSharpAction("pagereloaded")')
      }


    }
    catch {

    }
    this.clearWebStoreConfigurationCache();
    this.clearSignupStateStorage();
    localStorage.removeItem('user');
    localStorage.removeItem('lastLoginDT');
    localStorage.removeItem('showSetup');
    sessionStorage.removeItem('allow-store-setup-v2');
    sessionStorage.removeItem('allow-store-setup-offer');
    window.location.href = window.location.origin
    this.currentUserSource.next(null);
    this.currentUserObj = null
  }

  // Lightweight logout that clears stored auth without reloading the page.
  softLogout(): void {
    this.clearWebStoreConfigurationCache();
    this.clearSignupStateStorage();
    localStorage.removeItem('user');
    localStorage.removeItem('lastLoginDT');
    sessionStorage.removeItem('auto-login');
    sessionStorage.removeItem('allow-store-setup-v2');
    sessionStorage.removeItem('allow-store-setup-offer');
    this.currentUserSource.next(null);
    this.currentUserObj = null;
    // After soft logout, send the user back to signup flow (or home as fallback)
    setTimeout(() => {
      this._router
        .navigateByUrl('/signup-v6/step1', { replaceUrl: true })
        .catch(() => this._router.navigate(['/'], { replaceUrl: true }));
    }, 0);
  }

  getAllUsers(model) {
    const url = environment.AzureUserInfo;

    return this._bs.httpPost(url, JSON.stringify(model)).pipe(map(res => {
      return res;
    }))
  }

  //----------------------------
  createStaffUser(user: any) {
    let url = environment.AzureUserInfo
    return this._bs.httpPost(url, user).pipe(map(res => {
      if (res.success)
        this.getUserByUserName(this._globals.userInfo.username).subscribe(() => { })
      return res;
    }))
  }

  createStaffUser_ForSetupOnly(user: any, showSpinner: boolean = true) {
    let url = environment.BusinessBaseURL + environment.SetupBusiness
    return this._bs.httpPost(url, user, false, showSpinner).pipe(map(res => {
      if (res.success)
        this.getUserByUserName(this._globals.userInfo.username).subscribe(() => { })
      return res;
    }))
  }

  businessWizardComplete() {
    let url = environment.BusinessBaseURL + environment.SetBusinessWizardFinished;
    return this._bs.httpPost(url, JSON.stringify({ IsBusinessSetup: true })).pipe(map(response => {
      return response;
    }))
  }

  private isCompletingInStoreBusinessSetup = false;
  /** Sets IsBusinessSetup=true the moment a merchant is confirmed as selling in-store (POSLite/
   *  POSPro) — whether that's discovered the first time they land on the dashboard after signup,
   *  or later when they turn on the in-store channel from the setup checklist. Guarded on
   *  businessSetup[0].isBusinessSetup (the same flag auth.guard.ts reads to skip the setup wizard)
   *  so the SetBusinessWizardFinished call only ever fires once per business. */
  completeBusinessSetupForInStoreChannel(): void {
    const setup = this._globals.userInfo?.businessSetup?.[0];
    if (!setup || this.isCompletingInStoreBusinessSetup) return;
    if (setup.isBusinessSetup === true || setup.IsBusinessSetup === true) return;

    const hasInStoreChannel = !!(setup.hasPOS ?? setup.HasPOS ?? setup.hasPOSLite ?? setup.HasPOSLite);
    if (!hasInStoreChannel) return;

    this.isCompletingInStoreBusinessSetup = true;
    this.businessWizardComplete().subscribe({
      next: (res: any) => {
        this.isCompletingInStoreBusinessSetup = false;
        if (res?.success) {
          setup.isBusinessSetup = true;
          setup.IsBusinessSetup = true;
        }
      },
      error: () => { this.isCompletingInStoreBusinessSetup = false; }
    });
  }
  isFirstLoginComplete() {
    let url = environment.BusinessBaseURL + environment.SetBusinessWizardFinished;
    return this._bs.httpPost(url, JSON.stringify({ IsBusinessSetup: true, isFirstLogin: true, isTour: false })).pipe(map(response => {
      return response;
    }))
  }
  isTourComplete() {
    let url = environment.BusinessBaseURL + environment.SetBusinessWizardFinished;
    return this._bs.httpPost(url, JSON.stringify({ IsBusinessSetup: true, isFirstLogin: true, isTour: true })).pipe(map(response => {
      return response;
    }))
  }
  istest() {
    let url = environment.BusinessBaseURL + environment.SetBusinessWizardFinished;
    return this._bs.httpPost(url, JSON.stringify({ IsBusinessSetup: true, isFirstLogin: false, isTour: false })).pipe(map(response => {
      return response;
    }))
  }

  deactivateUser(userId: string, status: boolean, pageNumber: number, pageSize: number) {
    let url = environment.AzureUserInfo;

    return this._bs.httpPost(url, JSON.stringify({
      "Name": status ? "ACTIVATEUSER" : 'DEACTIVATEUSER',
      "User": {
        "UserID": userId,
      },
      "PageNo": pageNumber,
      "PageSize": pageSize
    })).pipe(map(response => {
      return response;
    }))
  }
}
